import { set, child, onValue } from 'firebase/database'
import { proxy, useSnapshot } from 'valtio'

import * as firebaseConnection from '../firebaseConnection'
import { type Floor, type Identifier } from '../model/floor-model'
import { type Mix } from '../model/mix'
import { type DeepReadonly } from '../typeUtils'
import { roomState, useSortedHumanParticipants } from '../../webrtc/webRTCRoom'

import { useMixes } from './useMixes'
import {
  type PanelState,
  panelState,
  usePanel,
  usePanelScreen,
} from './usePanel'
import { cardIsOfType, type TrayState, useTrayState } from './useTray'

const ROOT_NODE = 'floor'

const floorState = proxy<{ floor: Floor | null }>({
  floor: null,
})

const floorInitState = proxy<{ isFloorReady: boolean }>({
  isFloorReady: false,
})

// Subscribe
firebaseConnection.runWhenReady((connection) => {
  // Adds a Firebase listener that triggers when the floor changes.
  onValue(child(connection.room, ROOT_NODE), (snapshot) => {
    floorState.floor = snapshot.val()
    floorInitState.isFloorReady = true
  })
})

// Actions

/**
 * Sets the floor.
 * @param floor - The floor to be set.
 */
const setFloor = async (floor: Floor) => {
  const connection = await firebaseConnection.get()

  return set(child(connection.room, ROOT_NODE), floor).catch((err: unknown) => {
    connection.logError('SET_FLOOR', 'Failed to set the floor', err)
  })
}

/**
 * Switches to solo mode.
 * @param participantId - The id of the participant.
 * @param identifier - The identifier (camera, video, etc).
 */
const switchToSolo = (participantId: number, identifier: Identifier) =>
  setFloor({
    layout: 'solo',
    participantId,
    identifier,
  })

/**
 * Switches to panel mode.
 * @param participantIds - The ids of the participants on panel or auto if all available should be displayed
 */
const switchToPanel = () => {
  void setFloor({
    layout: 'panel',
  })
}

/** Switches to tray layout. */
const switchToTray = () =>
  setFloor({
    layout: 'tray',
  })

/**
 * Switches to mixed mode.
 * @param participantId - The id of the participant.
 * @param identifier - The identifier (camera, video, etc).
 */
const switchToSideBySide = (participantId: number, identifier: Identifier) =>
  setFloor({
    layout: 'side-by-side',
    identifier,
    participantId,
  })

const switchToCustomMix = (mixId: string) =>
  setFloor({
    layout: 'custom',
    mixId,
  })

const useFloorState = () => useSnapshot(floorState).floor

const useIsFloorReadyState = () => useSnapshot(floorInitState).isFloorReady

const useIsVideoShowing = () => {
  const { floor } = useSnapshot(floorState)
  const panelScreen = usePanelScreen()
  return (
    (floor?.layout === 'solo' &&
      floor.identifier === 'camera' &&
      floor.participantId === 0) ||
    (floor?.layout === 'panel' && panelScreen === 'tray')
  )
}

const findMixMediaFeeds = (mixId: string, mixes: DeepReadonly<Mix[]>) =>
  mixes
    .find((m) => m.id === mixId)
    ?.feeds.flatMap((f) => (f.media ? [f.media] : [])) ?? []

const isParticipantCameraInFloor = (
  participantId: number,
  floor: Floor | null,
  panel: PanelState,
  mixes: DeepReadonly<Mix[]>
) => {
  if (!floor) return false

  switch (floor.layout) {
    case 'solo':
      return (
        floor.identifier === 'camera' && floor.participantId === participantId
      )
    case 'side-by-side':
      return floor.participantId === participantId
    case 'panel':
      return (
        panel.mode === 'auto' || panel.participantIds.includes(participantId)
      )
    case 'custom':
      return findMixMediaFeeds(floor.mixId, mixes).some(
        (media) => media.type === 'camera' && media.id === participantId
      )
    case 'tray':
    default:
      return false
  }
}

const isParticipantCameraAloneInFloor = (
  participantId: number,
  floor: Floor | null,
  panel: PanelState,
  mixes: DeepReadonly<Mix[]>
) => {
  if (!floor) return false

  switch (floor.layout) {
    case 'solo':
      return (
        floor.identifier === 'camera' && floor.participantId === participantId
      )
    case 'panel':
      return (
        (panel.mode === 'auto' && roomState.participants.length === 1) ||
        (panel.mode === 'manual' &&
          panel.participantIds.every((id) => id === participantId))
      )
    case 'custom': {
      const mediaFeeds = findMixMediaFeeds(floor.mixId, mixes)
      return (
        mediaFeeds.length > 0 &&
        mediaFeeds.every(
          (media) => media.type === 'camera' && media.id === participantId
        )
      )
    }
    case 'side-by-side':
    case 'tray':
    default:
      return false
  }
}

const useIsParticipantCameraInFloor = (participantId: number) => {
  const floor = useFloorState()
  const panel = usePanel()
  const mixes = useMixes()

  return isParticipantCameraInFloor(participantId, floor, panel, mixes)
}

const isParticipantScreenInFloor = (
  participantId: number,
  floor: Floor | null,
  panel: PanelState,
  tray: DeepReadonly<TrayState>,
  mixes: DeepReadonly<Mix[]>
) => {
  if (!floor) return false

  switch (floor.layout) {
    case 'solo':
      return (
        floor.identifier === 'screenshare' &&
        floor.participantId === participantId
      )
    case 'side-by-side':
      return (
        floor.identifier === 'screenshare' &&
        floor.participantId === participantId
      )
    case 'panel':
      if (
        participantId === 0 &&
        panel.screen === 'tray' &&
        cardIsOfType(tray.lastActiveMediaCard, 'video')
      ) {
        return true
      }
      return panel.screen === participantId
    case 'tray': {
      return (
        participantId === 0 && cardIsOfType(tray.lastActiveMediaCard, 'video')
      )
    }
    case 'custom':
      return findMixMediaFeeds(floor.mixId, mixes).some(
        (media) =>
          (media.type === 'screen' && media.id === participantId) ||
          (media.type === 'tray' &&
            participantId === 0 &&
            cardIsOfType(tray.lastActiveMediaCard, 'video'))
      )
    default:
      return false
  }
}

const isParticipantScreenAloneInFloor = (
  participantId: number,
  floor: Floor | null,
  panel: PanelState,
  tray: TrayState,
  mixes: DeepReadonly<Mix[]>
) => {
  if (!floor) return false
  switch (floor.layout) {
    case 'solo':
      return (
        floor.identifier === 'screenshare' &&
        floor.participantId === participantId
      )
    case 'panel':
      if (panel.participantIds.length > 0) return false
      if (
        participantId === 0 &&
        panel.screen === 'tray' &&
        cardIsOfType(tray.lastActiveMediaCard, 'video')
      )
        return true
      return panel.screen === participantId
    case 'tray':
      return (
        participantId === 0 && cardIsOfType(tray.lastActiveMediaCard, 'video')
      )
    case 'custom': {
      const mediaFeeds = findMixMediaFeeds(floor.mixId, mixes)
      return (
        mediaFeeds.length > 0 &&
        mediaFeeds.every(
          (media) =>
            (media.type === 'screen' && media.id === participantId) ||
            (media.type === 'tray' &&
              participantId === 0 &&
              cardIsOfType(tray.lastActiveMediaCard, 'video'))
        )
      )
    }
    case 'side-by-side':
    default:
      return false
  }
}

const useIsParticipantScreenInFloor = (participantId: number) => {
  const floor = useFloorState()
  const panel = usePanel()
  const tray = useTrayState()
  const mixes = useMixes()
  return isParticipantScreenInFloor(participantId, floor, panel, tray, mixes)
}

const isParticipantMobileInFloor = (
  participantId: number,
  floor: Floor | null,
  mixes: DeepReadonly<Mix[]>
) => {
  if (!floor) return false

  switch (floor.layout) {
    case 'solo':
      return (
        floor.identifier === 'mobile' && floor.participantId === participantId
      )
    case 'side-by-side':
      return (
        floor.identifier === 'mobile' && floor.participantId === participantId
      )
    case 'custom':
      return findMixMediaFeeds(floor.mixId, mixes).some(
        (media) => media.type === 'mobile' && media.id === participantId
      )
    case 'panel':
    case 'tray':
    default:
      return false
  }
}

const useIsParticipantMobileInFloor = (participantId: number) => {
  const floor = useFloorState()
  const mixes = useMixes()
  return isParticipantMobileInFloor(participantId, floor, mixes)
}

const useIsTrayInFloor = () => {
  const feeds = useFeedsInFloor()

  return feeds.some((f) => f.type === 'tray')
}

const isFloorValid = (participantsIds: number[]) => {
  const { floor } = floorState
  const panel = panelState
  if (!floor) return false

  switch (floor.layout) {
    case 'solo':
    case 'side-by-side': {
      return participantsIds.includes(floor.participantId)
    }
    case 'panel': {
      if (panel.mode === 'auto') return true
      return panel.participantIds.some((id) => participantsIds.includes(id))
    }
    default:
      return true
  }
}

type Feed = { type: 'tray' | 'camera' | 'screen' | 'mobile'; id: number }

const getFeedsInFloor = (
  floor: Floor | null,
  participants: readonly { id: number }[],
  panel: PanelState,
  mixes: DeepReadonly<Mix[]>
): Feed[] => {
  if (!floor) return []

  switch (floor.layout) {
    case 'solo': {
      const participant = participants.find((p) => p.id === floor.participantId)
      if (!participant) return []
      switch (floor.identifier) {
        case 'camera':
          return [{ type: 'camera', id: participant.id }]
        case 'screenshare':
          return [{ type: 'screen', id: participant.id }]
        case 'mobile':
          return [{ type: 'mobile', id: participant.id }]
        default:
          return []
      }
    }
    case 'side-by-side': {
      const participant = participants.find((p) => p.id === floor.participantId)
      if (!participant) return []
      const feeds: Feed[] = [{ type: 'camera', id: participant.id }]
      switch (floor.identifier) {
        case 'screenshare':
          feeds.push({ type: 'screen', id: participant.id })
          break
        case 'mobile':
          feeds.push({ type: 'mobile', id: participant.id })
          break
      }
      return feeds
    }

    case 'tray': {
      return [{ type: 'tray', id: 0 }]
    }

    case 'panel': {
      const feeds: Feed[] = []
      participants.forEach((p) => {
        if (panel.mode === 'auto' || panel.participantIds.includes(p.id)) {
          feeds.push({ type: 'camera', id: p.id })
        }
      })
      if (panel.screen === 'tray') {
        feeds.push({ type: 'tray', id: 0 })
      } else if (panel.screen) {
        feeds.push({ type: 'screen', id: panel.screen })
      }
      return feeds
    }

    case 'custom': {
      const mix = mixes.find((m) => m.id === floor.mixId)
      if (!mix) return []
      const feeds: Feed[] = []
      mix.feeds.forEach((f) => {
        if (!f.media) {
          // @ts-expect-error — TODO: type properly
          feeds.push({})
          return
        }

        if (f.media.type === 'tray') {
          feeds.push({ type: 'tray', id: 0 })
        } else {
          feeds.push({ type: f.media.type, id: f.media.id })
        }
      })
      return feeds
    }

    default:
      return []
  }
}

const useFeedsInFloor = (): Feed[] => {
  const floor = useFloorState()
  const participants = useSortedHumanParticipants()
  const panel = usePanel()
  const mixes = useMixes()

  return getFeedsInFloor(floor, participants, panel, mixes)
}

const useAllFeedsGrouped = () => {
  const participants = useSortedHumanParticipants()

  const groupedFeeds: Record<
    'camera' | 'screen' | 'mobile' | 'tray',
    (Feed & { name?: string })[]
  > = {
    camera: [],
    screen: [],
    mobile: [],
    tray: [],
  }

  participants.forEach((p) => {
    if (p.tracks.camera && p.tracks.camera.isEnabled) {
      groupedFeeds.camera.push({ type: 'camera', id: p.id, name: p.name })
    }

    if (p.tracks.screen && p.tracks.screen.isEnabled) {
      groupedFeeds.screen.push({ type: 'screen', id: p.id, name: p.name })
    }

    if (p.tracks.mobile && p.tracks.mobile.isEnabled) {
      groupedFeeds.mobile.push({ type: 'mobile', id: p.id, name: p.name })
    }
  })

  groupedFeeds.tray.push({ type: 'tray', id: 0 })

  return groupedFeeds
}

const useAllFeeds = () => {
  const feedsGrouped = useAllFeedsGrouped()

  const feeds: Feed[] = []

  feedsGrouped.camera.forEach((f) => feeds.push(f))
  feedsGrouped.screen.forEach((f) => feeds.push(f))
  feedsGrouped.mobile.forEach((f) => feeds.push(f))
  feedsGrouped.tray.forEach((f) => feeds.push(f))
}

export {
  floorState,
  floorInitState,
  useFloorState,
  useIsFloorReadyState,
  switchToSolo,
  switchToSideBySide,
  switchToPanel,
  switchToTray,
  switchToCustomMix,
  isFloorValid,
  useIsVideoShowing,
  isParticipantCameraInFloor,
  isParticipantScreenInFloor,
  isParticipantMobileInFloor,
  isParticipantCameraAloneInFloor,
  isParticipantScreenAloneInFloor,
  useIsParticipantCameraInFloor,
  useIsParticipantScreenInFloor,
  useIsParticipantMobileInFloor,
  useIsTrayInFloor,
  getFeedsInFloor,
  useFeedsInFloor,
  useAllFeedsGrouped,
  useAllFeeds,
  type Feed as FloorFeedRef,
}
